
import Vue from 'vue';
import ClusterRenderer from './MarkerClusterRenderer';
import throttle from 'lodash/throttle';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { BusinessLocation } from '@/types/global';
import iconMap from '@/assets/svg/icons/iconMap';

export default Vue.extend({
  props: {
    locations: {
      type: [],
      default: () => [],
    },
    initialSetup: {
      type: Object,
      required: false,
    },
  },

  data() {
    return {
      storybookEnabled: process.env.STORYBOOK_ENABLED || false,
      consentForGoogleMapsGiven: process.env.STORYBOOK_ENABLED ? true : false,
      loadingMap: false as boolean,
      mapCreated: false as boolean,
      approachUrl: '' as string,
      mapsApiKey: '' as string,
      map: null as google.maps.Map | null | undefined,
      mapOptions: null as google.maps.MapOptions | null | undefined,
      center: null as google.maps.LatLngLiteral | null | undefined,
      markers: [] as any,
      activeMarker: null as any,
      activeInfoWindow: null as any,
      infoWindowCt: null as HTMLElement | null,
      infoContent: null as any,
      defaultIcon: null as google.maps.Icon | null | undefined,
      activeIcon: null as google.maps.Icon | null | undefined,
      bounds: null as google.maps.LatLngBounds | null | undefined,
      throttledResize: null as any,
      currentBreakpoint: '' as string,
      mqlMobile: window.matchMedia('(max-width: 559px)') as MediaQueryList,
      mqlTablet: window.matchMedia('(min-width: 560px) and (max-width: 1023px)') as MediaQueryList,
      mqlDesktop: window.matchMedia('(min-width: 1024px)') as MediaQueryList,
      initMap: () => true,
    };
  },

  mounted() {
    this.throttledResize = throttle(this.resizeEvent, 200);
    window.addEventListener('resize', this.throttledResize, { passive: true });

    this.mqlMobile.addEventListener('change', this.adjustZoomLevel, { passive: true });
    this.mqlTablet.addEventListener('change', this.adjustZoomLevel, { passive: true });
    this.mqlDesktop.addEventListener('change', this.adjustZoomLevel, { passive: true });
  },
  destroyed() {
    window.removeEventListener('resize', this.throttledResize);
  },

  methods: {
    loadOrCreateMap() {
      if (this.hasMapsApiJs()) {
        this.createMap();
      } else {
        this.loadGoogleMapsJs();
      }
    },
    loadOrBlockGoogleMaps(e: any): void {
      this.consentForGoogleMapsGiven = e;
      e ? this.loadOrCreateMap() : null;
    },
    resizeEvent() {
      if (this.activeInfoWindow) {
        this.closeInfoWindow();
      }
    },
    hasMapsApiJs() {
      return !!document.head.querySelector('#c-maps-api-js');
    },
    loadGoogleMapsJs() {
      // @ts-ignore
      if (!this.loadingMap) {
        this.initGoogleMapsApi();
      }
    },

    initGoogleMapsApi() {
      this.loadingMap = true;
      if (process.env.GOOGLE_MAPS_API_KEY) {
        this.mapsApiKey = process.env.GOOGLE_MAPS_API_KEY;
      }

      if (this.mapsApiKey) {
        const jsFileTag: HTMLElement = document.createElement('script');

        jsFileTag.setAttribute('type', 'text/javascript');
        jsFileTag.setAttribute('src', 'https://maps.googleapis.com/maps/api/js?key=' + this.mapsApiKey);
        jsFileTag.setAttribute('id', 'c-maps-api-js');
        jsFileTag.onload = async () => {
          await this.createMap();
        };

        if (!this.mapCreated && !this.hasMapsApiJs()) {
          document.head.appendChild(jsFileTag);
        }
      }
    },

    adjustZoomLevel(e: MediaQueryListEvent) {
      if (this.map) {
        const currentZoom = this.map.getZoom() as number;
        if (e.target === this.mqlMobile && e.matches) {
          if (this.currentBreakpoint === 'desktop') {
            this.map.setZoom(currentZoom - 2);
          } else {
            this.map.setZoom(currentZoom - 1);
          }
          this.currentBreakpoint = 'mobile';
        } else if (e.target === this.mqlTablet && e.matches) {
          if (this.currentBreakpoint === 'mobile') {
            this.map.setZoom(currentZoom + 1);
          } else if (this.currentBreakpoint === 'desktop') {
            this.map.setZoom(currentZoom - 1);
          }
          this.currentBreakpoint = 'tablet';
        } else if (e.target === this.mqlDesktop && e.matches) {
          if (this.currentBreakpoint === 'mobile') {
            this.map.setZoom(currentZoom + 2);
          } else {
            this.map.setZoom(currentZoom + 1);
          }
          this.currentBreakpoint = 'desktop';
        }
      }
    },

    async createMap() {
      this.mapCreated = true;
      let zoom = 16;
      const mapContainer = this.$refs?.mapContainer as HTMLElement;
      const locations = this.locations as Array<BusinessLocation>;

      if (this.initialSetup && this.initialSetup.zoom) {
        zoom = parseInt(this.initialSetup.zoom);
      }

      if (this.mqlTablet.matches) {
        zoom -= 1;
        this.currentBreakpoint = 'tablet';
      } else if (this.mqlMobile.matches) {
        zoom -= 2;
        this.currentBreakpoint = 'mobile';
      } else {
        this.currentBreakpoint = 'desktop';
      }

      if (this.initialSetup && this.initialSetup.latitude && this.initialSetup.longitude) {
        this.center = {
          lat: parseFloat(this.initialSetup.latitude),
          lng: parseFloat(this.initialSetup.longitude),
        };
      } else {
        this.center = {
          lat: locations[0].position.lat,
          lng: locations[0].position.lng,
        };
      }

      this.mapOptions = {
        gestureHandling: 'cooperative',
        disableDoubleClickZoom: true,
        clickableIcons: false,
        disableDefaultUI: true,
        maxZoom: 18,
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_BOTTOM,
        },
        zoom,
        center: this.center,
        styles: require('./mapstyle.json'),
        backgroundColor: '#35AFA9',
      };

      this.defaultIcon = {
        url: '/img/maps/address-marker.svg',
        scaledSize: new google.maps.Size(35, 48),
        anchor: new google.maps.Point(17, 48), // = bottom-center
        labelOrigin: new google.maps.Point(17, 20),
      };
      this.activeIcon = {
        url: '/img/maps/address-marker-active.svg',
        scaledSize: new google.maps.Size(35, 48),
        anchor: new google.maps.Point(17, 48), // = bottom-center
        labelOrigin: new google.maps.Point(17, 20),
      };

      this.infoWindowCt = this.$el.querySelector('.c-map__info-window');

      this.map = new google.maps.Map(mapContainer, this.mapOptions);

      this.map.addListener('zoom_changed', () => {
        if (this.map) {
          const currentZoom = this.map.getZoom() as number;
          if (currentZoom > 12) {
            this.map.setOptions({
              styles: require('./mapstyle_rich.json'),
            });
          } else {
            this.map.setOptions({
              styles: require('./mapstyle.json'),
            });
          }
          if (this.activeInfoWindow) {
            this.closeInfoWindow();
          }
        }
      });

      this.loadingMap = false;
      await this.addMarkers();
      if (
        !this.initialSetup ||
        (this.initialSetup &&
          (this.initialSetup.zoom === '0' || !this.initialSetup.zoom) &&
          !this.initialSetup.latitude &&
          !this.initialSetup.longitude)
      ) {
        this.setMapBounds();
      }
    },
    getPosString(location: BusinessLocation): string {
      return location.position.lat + ' ' + location.position.lng;
    },
    checkUniqueLocations(locations: Array<BusinessLocation>): Array<BusinessLocation> {
      locations.forEach((location: BusinessLocation) => {
        const locationsAtPos = this.locations.filter((item: BusinessLocation) => {
          return this.getPosString(location) === this.getPosString(item);
        });
        location.isUnique = locationsAtPos.length === 1;
        location.locationsAtPos = locationsAtPos;
      });
      return locations;
    },
    addMarkers(): Promise<void> {
      return new Promise(resolve => {
        const locations = this.checkUniqueLocations(this.locations as Array<BusinessLocation>);

        locations.forEach((location: BusinessLocation) => {
          let marker: google.maps.Marker;
          if (!location.isUnique) {
            marker = new google.maps.Marker({
              position: {
                lat: location.position.lat,
                lng: location.position.lng,
              },
              label: {
                text: `${location.locationsAtPos.length}`,
                color: 'white',
                fontFamily: 'Bauhaus',
                fontSize: '16px',
                fontWeight: '600',
              },
              map: this.map,
              icon: this.defaultIcon,
            });
          } else {
            marker = new google.maps.Marker({
              position: {
                lat: location.position.lat,
                lng: location.position.lng,
              },
              label: null,
              map: this.map,
              icon: this.defaultIcon,
            });
          }

          google.maps.event.addListener(marker, 'click', (e: Event) => {
            this.markerClickHandler(marker, location);
          });

          google.maps.event.addListener(marker, 'mouseover', () => {
            this.markerEnterHandler(marker);
          });

          google.maps.event.addListener(marker, 'mouseout', () => {
            this.markerLeaveHandler(marker);
          });

          this.markers.push(marker);
        });

        // Add a marker clusterer to manage the markers.
        new MarkerClusterer({
          map: this.map,
          markers: this.markers,
          renderer: ClusterRenderer,
        });

        resolve();
      });
    },
    setMapBounds() {
      this.bounds = new google.maps.LatLngBounds();
      this.markers.forEach((marker: google.maps.Marker) => {
        const position = marker.getPosition();
        if (position) {
          this.bounds?.extend(position);
        }
      });
      if (this.map) {
        this.map.fitBounds(this.bounds, { bottom: 100, top: 100, left: 50, right: 50 });
      }
    },
    markerClickHandler(marker: google.maps.Marker, location: BusinessLocation) {
      this.resetMarker(this.activeMarker);
      this.setMarkerActive(marker);
      this.openInfoWindow(location.locationsAtPos);
    },
    markerEnterHandler(marker: google.maps.Marker) {
      if (marker !== this.activeMarker) {
        marker.setIcon(this.activeIcon);
      }
    },
    markerLeaveHandler(marker: google.maps.Marker) {
      if (marker !== this.activeMarker) {
        marker.setIcon(this.defaultIcon);
      }
    },
    resetMarker(marker: google.maps.Marker) {
      if (marker) {
        marker.setIcon(this.defaultIcon);
        this.activeMarker = null;
      }
    },
    setMarkerActive(marker: google.maps.Marker) {
      marker.setIcon(this.activeIcon);
      this.activeMarker = marker;
    },
    sanitizeAreaName(name: string): string {
      const regexAreaName = /[äöüß\- ]/g;
      const tr: any = {
        ä: 'ae',
        ü: 'ue',
        ö: 'oe',
        ß: 'ss',
        '-': '',
        ' ': '',
      };
      return name.replace(regexAreaName, function ($0: string) {
        return tr[$0];
      });
    },
    openInfoWindow(infoData: Array<BusinessLocation>) {
      this.infoContent = infoData;

      this.infoContent.forEach((infoItem: BusinessLocation) => {
        if (infoItem.businessareas?.length) {
          let areaName = 'logo-' + this.sanitizeAreaName(infoItem.businessareas[0].toLowerCase());
          infoItem.logoName = iconMap[areaName] ? areaName : 'logo-default';
        } else {
          infoItem.logoName = 'logo-default';
        }

        if (infoItem.address && infoItem.city) {
          infoItem.approachUrl =
            `https://www.google.com/maps/dir//` +
            encodeURIComponent(infoItem.address + `,` + infoItem.city) +
            `/@${infoItem.position.lat},${infoItem.position.lng},12z`;
        } else {
          infoItem.approachUrl = `https://www.google.com/maps/@${infoItem.position.lat},${infoItem.position.lng},12z`;
        }
      });

      if (this.activeInfoWindow) {
        this.activeInfoWindow.close();
      }

      this.activeInfoWindow = null;
      this.activeInfoWindow = new google.maps.InfoWindow({
        content: this.infoWindowCt,
        maxWidth: 332,
        zIndex: 1,
      });

      google.maps.event.addListener(this.activeInfoWindow, 'visible', () => {
        const infoWindowElement = this.$el.querySelector('.c-map__info-window') as HTMLElement;
        if (infoWindowElement) {
          infoWindowElement.setAttribute('data-visible', '');
          if (this.infoWindowCt) {
            const infoWindowWidth = this.infoWindowCt.getBoundingClientRect().width + 100;
            const infoWindowHeight = this.infoWindowCt.getBoundingClientRect().height + 50;
            const positionX = Math.floor(infoWindowWidth * 0.5);
            const positionY = Math.floor(infoWindowHeight * 0.5);
            this.activeInfoWindow.setOptions({
              pixelOffset: new google.maps.Size(positionX, positionY),
            });
          }
          document.addEventListener('keydown', this.keyDownHandler, { passive: true });

          const infoWindowParent = infoWindowElement.parentElement as HTMLElement;
          infoWindowElement.style.width = infoWindowParent.getBoundingClientRect().width + 'px';
          infoWindowElement.style.overflowY = 'auto';
          infoWindowParent.style.overflow = 'hidden';
        }
      });

      google.maps.event.addListener(this.activeInfoWindow, 'closeclick', () => {
        this.closeInfoWindow();
      });

      this.activeInfoWindow.open({
        map: this.map,
        anchor: this.activeMarker,
      });
    },
    closeInfoWindow() {
      this.activeInfoWindow.close();
      this.resetMarker(this.activeMarker);
      document.removeEventListener('keydown', this.keyDownHandler);
    },
    keyDownHandler(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        this.closeInfoWindow();
      }
    },
  },
});
